import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Switch, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import { LockedFeatureTooltip } from '../atoms';
import { CHANNEL_FEATURE_FLAGS, IntakeChannelsState } from '../types';

interface Props {
  value: IntakeChannelsState;
  onChange: (value: IntakeChannelsState) => void;
  forced: boolean;
  legacy?: boolean;
  manualIntake?: boolean;
  onManualIntakeChange?: (value: boolean) => void;
}

type ChannelKey = keyof IntakeChannelsState;
const CHANNELS: { key: ChannelKey; label: string }[] = [
  { key: 'vaultTalk', label: 'VaultTalk' },
  { key: 'slack', label: 'Slack' },
  { key: 'reportingPortal', label: 'Vault' },
];

export const IntakeChannelsCard: React.FC<Props> = ({
  value,
  onChange,
  forced,
  legacy,
  manualIntake,
  onManualIntakeChange,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const vaultTalkEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.vaultTalk,
  );
  const slackEntitled = useIsFeatureEnabled(CHANNEL_FEATURE_FLAGS.slack);
  const reportingPortalEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.reportingPortal,
  );
  const entitled: Record<ChannelKey, boolean> = {
    vaultTalk: vaultTalkEntitled,
    slack: slackEntitled,
    reportingPortal: reportingPortalEntitled,
  };

  useEffect(() => {
    const needsSync = (Object.keys(entitled) as ChannelKey[]).some(
      (key) => !entitled[key] && value[key],
    );
    if (needsSync) {
      onChange({
        vaultTalk: value.vaultTalk && entitled.vaultTalk,
        slack: value.slack && entitled.slack,
        reportingPortal: value.reportingPortal && entitled.reportingPortal,
      });
    }
  }, [entitled.vaultTalk, entitled.slack, entitled.reportingPortal]); // eslint-disable-line react-hooks/exhaustive-deps

  const channelState = (key: ChannelKey) => {
    if (forced) {
      return key === 'reportingPortal'
        ? { checked: true, disabled: true, locked: false }
        : { checked: false, disabled: true, locked: false };
    }
    if (!entitled[key]) return { checked: false, disabled: true, locked: true };
    return { checked: value[key], disabled: false, locked: false };
  };

  return (
    <Box
      data-testid="intake-channels-card"
      sx={{
        border: `1px solid ${color.ui.divider.default.value}`,
        borderRadius: tokens.semantic.radius.md.value,
        padding: tokens.semantic.spacing.desktop.gap[2].value,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <Typography
        sx={{
          fontWeight: tokens.semantic.fontWeight.emphasis.value,
          fontSize: '16px',
        }}
      >
        Intake channels
      </Typography>
      <Typography sx={{ fontSize: '12px', color: color.type.muted.value }}>
        Select where this category should be available across intake channels.
      </Typography>
      {legacy && onManualIntakeChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Switch
            data-testid="channel-manualIntake"
            checked={manualIntake ?? false}
            onChange={(e) => onManualIntakeChange(e.target.checked)}
            slotProps={{
              input: {
                'aria-label': 'Manual Intake',
                sx: { width: '100% !important', height: '100% !important' },
              },
            }}
          />
          <Typography sx={{ fontSize: '14px' }}>Manual Intake</Typography>
        </Box>
      )}
      {CHANNELS.map(({ key, label }) => {
        const s = channelState(key);
        return (
          <Box
            key={key}
            sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Switch
              data-testid={`channel-${key}`}
              checked={s.checked}
              disabled={s.disabled}
              onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
              slotProps={{
                input: {
                  'aria-label': label,
                  sx: { width: '100% !important', height: '100% !important' },
                },
              }}
            />
            <Typography sx={{ fontSize: '14px' }}>{label}</Typography>
            {s.locked && (
              <span data-testid={`channel-lock-${key}`}>
                <LockedFeatureTooltip />
              </span>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
